"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

type Line = {
  label: { en: string; es: string };
  amount: string;
  note?: { en: string; es: string };
  strike?: boolean;
};

const DOORDASH_LINES: Line[] = [
  {
    label: { en: "Menu, marked up 20%", es: "Menú, remarcado 20%" },
    amount: "$54.00",
    note: { en: "the $45 order, priced up to cover commission", es: "el pedido de $45, subido para cubrir la comisión" },
  },
  {
    label: { en: "Delivery fee", es: "Fee de envío" },
    amount: "$2.99",
    note: { en: "the small fee the diner sees", es: "el fee chiquito que ve el cliente" },
  },
];

const REDY_LINES: Line[] = [
  {
    label: { en: "Menu, your real prices", es: "Menú, tus precios reales" },
    amount: "$45.00",
    note: { en: "the same $45, untouched", es: "los mismos $45, sin tocar" },
  },
  {
    label: { en: "Delivery fee", es: "Fee de envío" },
    amount: "$7.99",
    note: { en: "example live courier quote", es: "ejemplo de cotización en vivo del courier" },
  },
];

export default function DeliveryFeeObjection() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-redy-charcoal text-white overflow-hidden">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.16) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1080px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[780px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F4722B" }}>
            {t("The objection you're about to have", "La objeción que estás por hacer")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("\"But the apps show", "\"Pero las apps muestran")}
            <br />
            <span className="text-white/30">{t("a smaller fee.\"", "un fee más chico.\"")}</span>
          </h2>
          <p className="text-[16px] md:text-[17px] font-medium text-white/60 leading-relaxed mt-6">
            {t(
              "Sometimes they do. And your diner will notice that the delivery line on your page is the real courier quote. Here's the honest answer, which happens to also be the good one: restaurants often raise their prices on marketplace apps to cover the commission. The fee is the part you see. The markup is the part you don't.",
              "A veces sí. Y tu cliente va a ver que la línea de envío en tu página es la cotización real del courier. Aquí va la respuesta honesta, que además resulta ser la buena: los restaurantes suelen subir sus precios en las apps de marketplace para cubrir la comisión. El fee es la parte que se ve. El remarcado es la que no."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-[860px] mx-auto">
          <ReceiptCard
            kicker={t("What the diner pays on a marketplace app", "Lo que el cliente paga en una app de marketplace")}
            brand={t("MARKETPLACE APP", "APP DE MARKETPLACE")}
            lines={DOORDASH_LINES}
            total="$56.99"
            highlight={false}
            delay={0.1}
            fromX={-30}
          />
          <ReceiptCard
            kicker={t("What the diner pays on your page", "Lo que el cliente paga en tu página")}
            brand={t("DIRECT", "DIRECTO")}
            lines={REDY_LINES}
            total="$52.99"
            highlight
            delay={0.25}
            fromX={30}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-16 max-w-[720px] mx-auto text-center"
        >
          <div className="inline-block rounded-[24px] px-8 py-7 border border-white/10 bg-white/[0.04]">
            <div className="text-[11px] font-black tracking-widest uppercase text-white/40 mb-3">
              {t("In this example, the diner pays less with you", "En este ejemplo, el cliente paga menos contigo")}
            </div>
            <div className="text-[40px] md:text-[52px] font-black tabular-nums leading-none">
              <span className="text-white/35 line-through decoration-[3px]">$56.99</span>
              <span className="text-white/25 mx-3">→</span>
              <span
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #FCA17D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                $52.99
              </span>
            </div>
            <p className="text-[14px] font-medium text-white/60 mt-5 leading-relaxed max-w-[520px] mx-auto">
              {t(
                "$4 cheaper — even though the delivery line reads higher. Say it out loud on your page: our menu is our menu, and the delivery line is what the courier actually costs. Nothing is hidden inside the food price.",
                "$4 más barato — aunque la línea de envío diga más. Dilo en voz alta en tu página: nuestro menú es nuestro menú, y la línea de envío es lo que el courier de verdad cuesta. Nada está escondido dentro del precio de la comida."
              )}
            </p>
          </div>

          <p className="text-[12px] font-medium text-white/35 mt-8 leading-relaxed max-w-[600px] mx-auto">
            {t(
              "Example only: a 20% menu markup on the marketplace side and a $7.99 courier quote on yours. How much restaurants mark up varies, and the real courier quote depends on distance and demand. Taxes and tip are the same on both sides, so they're left out.",
              "Solo un ejemplo: 20% de remarcado del lado del marketplace y una cotización de courier de $7.99 del tuyo. Cuánto remarcan los restaurantes varía, y la cotización real del courier depende de la distancia y la demanda. Impuestos y propina son iguales de los dos lados, así que se dejan fuera."
            )}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function ReceiptCard({
  kicker,
  brand,
  lines,
  total,
  highlight,
  delay,
  fromX,
}: {
  kicker: string;
  brand: string;
  lines: Line[];
  total: string;
  highlight: boolean;
  delay: number;
  fromX: number;
}) {
  const { t } = useLang();

  return (
    <motion.div
      initial={{ opacity: 0, x: fromX }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      className={`relative rounded-[24px] p-8 border overflow-hidden ${
        highlight ? "bg-white/[0.06] border-white/15" : "bg-white/[0.02] border-white/10"
      }`}
      style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
    >
      {highlight && (
        <div
          className="absolute -top-10 -right-10 w-[200px] h-[200px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(244,114,43,0.18) 0%, transparent 70%)" }}
        />
      )}

      <div className="relative">
        <div className="text-center pb-4 border-b border-dashed border-white/15 mb-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">
            {kicker}
          </div>
          <div className="text-[18px] font-black mt-1 text-white">{brand}</div>
        </div>

        <div className="space-y-4">
          {lines.map((l, i) => (
            <div key={i}>
              <div className="flex justify-between items-baseline text-[13px] text-white/85">
                <span>{t(l.label.en, l.label.es)}</span>
                <span className="tabular-nums font-bold">{l.amount}</span>
              </div>
              {l.note && (
                <div className="text-[11px] text-white/35 mt-0.5">{t(l.note.en, l.note.es)}</div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-dashed border-white/15 my-5" />

        <div className="flex justify-between items-baseline">
          <span className="text-[12px] font-bold uppercase tracking-wider text-white/40">
            {t("Diner pays", "El cliente paga")}
          </span>
          <span
            className="text-[30px] font-black tabular-nums"
            style={highlight ? { color: "#F4722B" } : { color: "rgba(255,255,255,0.45)" }}
          >
            {total}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
